import type { PortalCommentPage } from '@/modules/client-portal/discussion-read-window'
import { threadApi } from '@/lib/api/threads.api'
import { readCompletePages } from '@/lib/query/read-complete-pages'
import { assertPortalCommentPage } from '@/modules/client-portal/discussion-read-window'

import type {
  ModuleTypeEnum,
  PaginatedResponse,
  Thread,
  ThreadComment,
  ThreadQueryParams,
} from '@scrambl/shared'
import {
  createPaginationMeta,
  isValidUUID,
  ThreadCommentVisibilityEnum,
  ThreadTypeEnum,
} from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

export interface ThreadReadFilters {
  page?: number
  limit?: number
  title?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  moduleType?: ModuleTypeEnum
  moduleId?: string
  threadType?: string
  membershipOnly?: boolean
  excludeComments?: boolean
}
export interface ThreadReaderContext {
  organizationId: string
  userId?: string
  portalEntityId: string | null
  signal: AbortSignal
}
export class ThreadReadContractError extends Error {
  constructor() {
    super('Discussions are unavailable')
    this.name = 'ThreadReadContractError'
  }
}

/** Failed authorization/ownership must not keep a previously readable copy visible.
 * Ordinary network/server outages may retain the last good data of this same scope.
 */
export function isThreadReadUnavailable(error: unknown): boolean {
  if (
    error instanceof ThreadReadContractError ||
    error instanceof SessionChangedError
  )
    return true
  const status =
    error && typeof error === 'object' && 'status' in error
      ? error.status
      : undefined
  return status === 401 || status === 403 || status === 404
}

export function threadListParams(
  filters: ThreadReadFilters = {},
): ThreadQueryParams {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 50,
  }
  for (const key of [
    'title',
    'sortBy',
    'sortOrder',
    'moduleType',
    'moduleId',
    'threadType',
  ] as const)
    if (filters[key] !== undefined) params[key] = filters[key]
  for (const key of ['membershipOnly', 'excludeComments'] as const)
    if (filters[key] !== undefined) params[key] = String(filters[key])
  return params as ThreadQueryParams
}
function requireIdentity(
  row: { id?: unknown; organizationId?: unknown },
  context: ThreadReaderContext,
): void {
  if (
    !row ||
    typeof row.id !== 'string' ||
    !isValidUUID(row.id) ||
    row.organizationId !== context.organizationId
  )
    throw new ThreadReadContractError()
}
function requirePage<T>(response: PaginatedResponse<T>, page: number): void {
  const meta = response?.meta
  if (
    !Array.isArray(response?.data) ||
    !meta ||
    meta.currentPage !== page ||
    !Number.isSafeInteger(meta.totalPages) ||
    meta.totalPages < 0 ||
    !Number.isSafeInteger(meta.totalItems) ||
    meta.totalItems < 0 ||
    typeof meta.hasNextPage !== 'boolean' ||
    meta.hasNextPage !== page < meta.totalPages ||
    (meta.totalPages === 0 && response.data.length > 0) ||
    (meta.hasNextPage && response.data.length === 0)
  )
    throw new ThreadReadContractError()
}

export const threadReadsApi = {
  async moduleComments(
    params: {
      moduleType: ModuleTypeEnum
      moduleId: string
      moduleName?: string
    },
    context: ThreadReaderContext,
  ): Promise<{ thread: Thread; page: PaginatedResponse<ThreadComment> }> {
    context.signal.throwIfAborted()
    if (context.portalEntityId) throw new ThreadReadContractError()
    const result = await threadApi.getOrCreateModuleThreadWithComments(
      { ...params, commentsLimit: 10 },
      { signal: context.signal },
    )
    context.signal.throwIfAborted()
    requireIdentity(result?.thread, context)
    if (
      result.thread.moduleType !== params.moduleType ||
      result.thread.moduleId !== params.moduleId ||
      result.thread.threadType !== ThreadTypeEnum.COMMENT ||
      !Array.isArray(result.comments) ||
      result.comments.length > 10 ||
      !Number.isSafeInteger(result.totalComments) ||
      result.totalComments < result.comments.length ||
      (result.totalComments > 0 && result.comments.length === 0)
    )
      throw new ThreadReadContractError()
    for (const row of result.comments) {
      requireIdentity(row, context)
      if (row.threadId !== result.thread.id || row.parentCommentId != null)
        throw new ThreadReadContractError()
    }
    const page = {
      data: result.comments,
      meta: createPaginationMeta(1, 10, result.totalComments),
    }
    requirePage(page, 1)
    return { thread: result.thread, page }
  },
  async list(
    filters: ThreadReadFilters,
    context: ThreadReaderContext,
  ): Promise<PaginatedResponse<Thread>> {
    context.signal.throwIfAborted()
    if (context.portalEntityId) throw new ThreadReadContractError()
    const response = await threadApi.getThreads(threadListParams(filters), {
      signal: context.signal,
      portalEntityId: null,
    })
    context.signal.throwIfAborted()
    requirePage(response, filters.page ?? 1)
    for (const row of response.data) {
      requireIdentity(row, context)
      if (
        (filters.moduleId && row.moduleId !== filters.moduleId) ||
        (filters.moduleType && row.moduleType !== filters.moduleType)
      )
        throw new ThreadReadContractError()
    }
    return response
  },
  async module(
    filters: ThreadReadFilters,
    context: ThreadReaderContext,
  ): Promise<Thread[]> {
    const rows = await readCompletePages(
      page => threadReadsApi.list({ ...filters, page, limit: 100 }, context),
      context.signal,
    )
    return [...new Map(rows.map(row => [row.id, row])).values()]
  },
  async detail(
    threadId: string,
    context: ThreadReaderContext,
  ): Promise<Thread> {
    context.signal.throwIfAborted()
    const row = await threadApi.getThread(threadId, {
      signal: context.signal,
      portalEntityId: context.portalEntityId,
    })
    context.signal.throwIfAborted()
    requireIdentity(row, context)
    if (row.id !== threadId) throw new ThreadReadContractError()
    return row
  },
  async comments(
    params: Parameters<typeof threadApi.getThreadComments>[0],
    context: ThreadReaderContext,
  ): Promise<PortalCommentPage> {
    context.signal.throwIfAborted()
    const response = await threadApi.getThreadComments(params, {
      signal: context.signal,
      portalEntityId: context.portalEntityId,
    })
    context.signal.throwIfAborted()
    requirePage(response, params.page ?? 1)
    let receipt
    if (context.portalEntityId) {
      try {
        receipt = assertPortalCommentPage(response, context, params)
      } catch {
        throw new ThreadReadContractError()
      }
    }
    for (const row of response.data) {
      requireIdentity(row, context)
      if (
        row.threadId !== params.threadId ||
        (params.parentCommentId !== undefined &&
          (row.parentCommentId ?? null) !== params.parentCommentId) ||
        (context.portalEntityId &&
          (row.visibility !== ThreadCommentVisibilityEnum.PUBLIC ||
            row.deletedAt))
      )
        throw new ThreadReadContractError()
    }
    return receipt ? { ...response, portalCommentReceipt: receipt } : response
  },
  async unreadCount(
    threadType: string | undefined,
    context: ThreadReaderContext,
  ): Promise<number> {
    context.signal.throwIfAborted()
    if (context.portalEntityId) throw new ThreadReadContractError()
    const response = await threadApi.getUnreadCount(threadType, {
      signal: context.signal,
    })
    context.signal.throwIfAborted()
    if (!Number.isSafeInteger(response?.count) || response.count < 0)
      throw new ThreadReadContractError()
    return response.count
  },
}
